<script>
  import { router, useForm } from '@inertiajs/svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FormLayout from '../../components/form_layout.svelte'
  import FormSection from '../../components/form_section.svelte'
  import FileButton from '../../components/file_button.svelte'
  import ScheduleEditor from '../../components/schedule_editor.svelte'
  import MultiSelect from '../../components/multi_select.svelte'
  import ImageSelector from '../../components/image_selector.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import TextareaField from '../../components/form/textarea_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import ToggleField from '../../components/form/toggle_field.svelte'
  import DateField from '../../components/form/date_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import AudienceUserList from '../../components/audience_user_list.svelte'
  import QuickCreateAdventureDrawer from '../../components/campaigns/quick_create_adventure_drawer.svelte'
  import QuickCreateFeedDrawer from '../../components/campaigns/quick_create_feed_drawer.svelte'
  import AppPreview from '../../components/campaigns/app_preview.svelte'
  import { Button } from '../../components/ui'
  import { liveValidate } from '../../lib/live_validation.svelte.js'

  // Thin form: it holds what the admin picks and types and posts it as-is. The server
  // (CampaignsController#campaign_params + Admin::CampaignService) builds the `_replace`
  // rows and positions and validates everything — live too, through Precognition
  // (`precognition!` in create/update), so no rule lives here. The card defaults by type,
  // the First Visit audience, the picks a new town or place drops and the town's time zone
  // come from the `form_defaults` partial reload; a CSV is parsed by `csv_audience`.
  let { ad, options = {}, areas = [], quick_create: quickCreate = {}, preview = [], submit_path, back_url } = $props()

  const isEdit = !!ad.id
  // On a duplicate, the parent drives the specific-days warning below (never submitted).
  const parentId = ad.ad_id

  const form = useForm(isEdit ? 'patch' : 'post', submit_path, {
    ad: {
      name: ad.name ?? '',
      area_id: ad.area_id ?? '',
      place_id: ad.place_id ?? '',
      ad_type: ad.ad_type ?? '',
      active: ad.active ?? true,
      theme: ad.theme ?? '',
      title: ad.title ?? '',
      savings: ad.savings ?? '',
      spend: ad.spend ?? '',
      photo_preference: ad.photo_preference ?? 'first_adventure',
      treasure_chest: ad.treasure_chest ?? false,
      display_as_new: ad.display_as_new ?? false,
      display_as_new_until: ad.display_as_new_until ?? '',
      audience_names: [...(ad.audience_names ?? ['everyone'])],
      user_ids: [...(ad.user_ids ?? [])],
      feed_ids: [...(ad.feed_ids ?? [])],
      adventure_details: (ad.adventure_details ?? []).map((detail) => ({ ...detail, images: (detail.images ?? []).map((image) => ({ ...image })) })),
      ad_images: (ad.ad_images ?? []).map((image) => ({ ...image })),
      schedule_attributes: { ...ad.schedule_attributes }
    },
    return_to: back_url
  })
  liveValidate(form, 'ad')

  // The adventure picker's value (its order follows the detail cards).
  let adventureIds = $state((ad.selected_adventures ?? []).map((adventure) => adventure.id))

  // Picking an adventure adds its detail card, seeded from the option's server payload
  // (old CMS addAdventureDetail); removing it drops the card.
  const addAdventureDetail = (option) => {
    if ($form.ad.adventure_details.some((detail) => detail.adventure_id === option.value)) return
    $form.ad.adventure_details = [
      ...$form.ad.adventure_details,
      {
        id: null,
        adventure_id: option.value,
        title: option.title || option.name || '',
        description: option.description || '',
        savings: option.savings || '',
        spend: option.spend || '',
        featured_photo_preference: 'daily_random',
        images: (option.images ?? []).map((image) => ({ ...image }))
      }
    ]
  }
  const removeAdventureDetail = (adventureId) => {
    $form.ad.adventure_details = $form.ad.adventure_details.filter((detail) => detail.adventure_id !== adventureId)
  }

  // The first adventure supplies the card's photos, so the admin can reorder the cards.
  const moveAdventure = (from, to) => {
    const details = [...$form.ad.adventure_details]
    const [detail] = details.splice(from, 1)
    details.splice(to, 0, detail)
    $form.ad.adventure_details = details
    adventureIds = details.map((item) => item.adventure_id)
  }

  // Records made in the "Create New" drawers join their picker already chosen (old CMS
  // onAdventureCreated / onFeedCreated); the server hands each back in the picker's option shape.
  let adventureOpen = $state(false)
  let feedOpen = $state(false)
  let createdAdventures = $state([])
  let createdFeeds = $state([])
  const onAdventureCreated = (option) => {
    createdAdventures = [...createdAdventures, option]
    adventureIds = [...adventureIds, option.value]
    addAdventureDetail(option)
    refreshDefaults([option.value])
  }
  const onFeedCreated = (option) => {
    createdFeeds = [...createdFeeds, option]
    $form.ad.feed_ids = [...$form.ad.feed_ids, option.value]
  }

  // The App Preview is built server-side (CampaignsController#campaign_preview) from the fields
  // it shows; a change re-requests it. `indices` keeps each detail's fields on its own row.
  const previewInput = $derived({
    feed_ids: $form.ad.feed_ids,
    theme: $form.ad.theme,
    title: $form.ad.title,
    savings: $form.ad.savings,
    photo_preference: $form.ad.photo_preference,
    ad_image_ids: $form.ad.ad_images.map((image) => image.id),
    details: $form.ad.adventure_details.map((detail) => ({
      title: detail.title,
      savings: detail.savings,
      image_ids: (detail.images ?? []).map((image) => image.id)
    }))
  })
  let previewKey = JSON.stringify(previewInput)
  let previewTimer = null
  $effect(() => {
    const key = JSON.stringify(previewInput)
    if (key === previewKey) return
    previewKey = key
    clearTimeout(previewTimer)
    previewTimer = setTimeout(() => {
      router.reload({
        only: ['preview'],
        data: { preview: JSON.parse(key) },
        queryStringArrayFormat: 'indices',
        // async: never cancels a save that's already on its way
        async: true,
        preserveUrl: true,
        preserveState: true,
        preserveScroll: true
      })
    }, 300)
  })

  const areaOptions = $derived(areas.map((area) => ({ value: area.id, label: area.name })))

  // Asks the server (CampaignsController#form_defaults) what changes with the picks: the card
  // copy and First Visit audience for newly picked adventures or a new type, the place, feeds
  // and adventures a new town or place no longer allows, and the town's time zone. A field
  // the admin edited while the request was out keeps their value.
  const DEFAULT_FIELDS = ['place_id', 'title', 'theme', 'savings', 'audience_names', 'feed_ids']
  const refreshDefaults = (addedAdventureIds = [], { townChanged = false } = {}) => {
    const sent = $form.ad
    const snapshot = JSON.stringify(DEFAULT_FIELDS.map((key) => sent[key]))
    const sentDetails = sent.adventure_details.map((detail) => detail.adventure_id).join()
    router.reload({
      only: ['form_defaults'],
      data: {
        defaults: {
          ...Object.fromEntries(['area_id', 'ad_type', ...DEFAULT_FIELDS].map((key) => [key, sent[key]])),
          added_adventure_ids: addedAdventureIds,
          adventure_details: sent.adventure_details.map(({ adventure_id, title, savings }) => ({ adventure_id, title, savings }))
        }
      },
      queryStringArrayFormat: 'indices',
      async: true,
      preserveUrl: true,
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        const defaults = page.props.form_defaults
        if (!defaults) return
        const current = $form.ad
        const fieldsUntouched = JSON.stringify(DEFAULT_FIELDS.map((key) => current[key])) === snapshot
        const next = { ...current }
        if (fieldsUntouched) DEFAULT_FIELDS.forEach((key) => (next[key] = defaults[key]))
        if (current.adventure_details.map((detail) => detail.adventure_id).join() === sentDetails) {
          next.adventure_details = current.adventure_details.filter((detail) => defaults.adventure_ids.includes(detail.adventure_id))
          adventureIds = adventureIds.filter((id) => defaults.adventure_ids.includes(id))
        }
        if (townChanged && defaults.time_zone) next.schedule_attributes = { ...current.schedule_attributes, time_zone: defaults.time_zone }
        $form.ad = next
      }
    })
  }

  let lastPicks = { area_id: $form.ad.area_id, place_id: $form.ad.place_id, ad_type: $form.ad.ad_type }
  $effect(() => {
    const picks = { area_id: $form.ad.area_id, place_id: $form.ad.place_id, ad_type: $form.ad.ad_type }
    if (JSON.stringify(picks) === JSON.stringify(lastPicks)) return
    const townChanged = picks.area_id !== lastPicks.area_id
    lastPicks = picks
    refreshDefaults([], { townChanged })
  })

  const setDetail = (index, key, value) => {
    $form.ad.adventure_details = $form.ad.adventure_details.map((detail, i) => (i === index ? { ...detail, [key]: value } : detail))
  }

  const audienceType = $derived($form.ad.audience_names.includes('everyone') ? 'everyone' : 'custom')
  const setAudienceType = (type) => {
    $form.ad.audience_names = type === 'everyone' ? ['everyone'] : $form.ad.audience_names.filter((name) => name !== 'everyone')
  }
  const audienceOptions = $derived(options.audience_options ?? [])
  const hasCsv = $derived($form.ad.audience_names.includes('uploaded_set'))

  // The server parses a picked CSV and merges its users into the list (csv_audience prop).
  let usersOpen = $state(false)
  let csv = $state({ summary: '', error: '' })
  const pickCsv = (event) => {
    const input = event.currentTarget
    const file = input.files?.[0]
    input.value = ''
    if (!file) return
    router.post('/campaigns/csv_audience', { csv: file, user_ids: $form.ad.user_ids }, {
      only: ['csv_audience'],
      preserveState: true,
      preserveScroll: true,
      preserveUrl: true,
      onStart: () => (csv = { summary: '', error: '' }),
      onSuccess: (page) => {
        const result = page.props.csv_audience
        if (!result) return
        $form.ad.user_ids = result.user_ids
        csv = { ...csv, summary: result.summary }
      },
      onError: (errors) => (csv = { ...csv, error: errors['ad.users_csv'] ?? 'The import failed.' })
    })
  }
  const clearUsers = () => {
    $form.ad.user_ids = []
    csv = { summary: '', error: '' }
  }

  const errorFor = (key) => {
    const value = $form.errors?.[`ad.${key}`]
    return Array.isArray(value) ? value[0] : value
  }

  const submit = () => $form.submit()

  const setAsInactive = () => {
    $form.ad.active = false
    submit()
  }
</script>

<Layout>
  <PageHeader
    title={isEdit ? 'Edit Campaign' : 'New Campaign'}
    subtitle="Configure targeting, content, audience, and schedule for this campaign."
  />

  <FormLayout
    onsubmit={(event) => {
      event.preventDefault()
      submit()
    }}
  >
    <FormSection title="Setup">
      <TextField bind:value={$form.ad.name} label="Campaign Name" name="name" error={errorFor('name')} required />
      <SelectField bind:value={$form.ad.area_id} label="College Town" name="area_id" options={areaOptions} error={errorFor('area_id')} required />
      <SelectField
        bind:value={$form.ad.place_id}
        label="Place"
        name="place_id"
        source="places"
        sourceParams={{ area_id: $form.ad.area_id }}
        error={errorFor('place_id')}
        required
      />
      <SelectField
        bind:value={$form.ad.ad_type}
        label="Type"
        name="ad_type"
        placeholder="Select a campaign type…"
        options={options.ad_types ?? []}
        error={errorFor('ad_type')}
        required
      />
    </FormSection>

    <FormSection title="Select Adventures">
      <p class="text-sm text-base-content/60">
        Pick the Adventures this campaign advertises. Each one gets its own section under Adventure Details below.
        {#if !$form.ad.place_id}Choose a Place in Setup first to narrow this list.{/if}
      </p>
      <MultiSelect
        bind:value={adventureIds}
        source="adventures"
        sourceParams={{ area_id: $form.ad.area_id, place_id: $form.ad.place_id }}
        selected={[
          ...(ad.selected_adventures ?? []).map((adventure) => ({
            value: adventure.id,
            label: adventure.title ? `${adventure.name} — ${adventure.title}` : adventure.name
          })),
          ...createdAdventures
        ]}
        label="Select from existing Adventures"
        placeholder="Search adventures…"
        onadd={(option) => {
          addAdventureDetail(option)
          refreshDefaults([option.value])
        }}
        onremove={removeAdventureDetail}
      />
      {#if errorFor('adventure_details')}<p class="text-error text-sm">{errorFor('adventure_details')}</p>{/if}
      <div>
        <Button variant="default" outline size="sm" icon="add" disabled={!quickCreate.can_create_adventure} onclick={() => (adventureOpen = true)}>Create New Adventure</Button>
      </div>
    </FormSection>

    <FormSection title="Campaign Card" description="The card members see in their feeds.">
      <MultiSelect
        bind:value={$form.ad.feed_ids}
        source="feeds"
        sourceParams={{ area_id: $form.ad.area_id, feed_type: 'standard' }}
        selected={[...(ad.selected_feeds ?? []).map((feed) => ({ value: feed.id, label: feed.name })), ...createdFeeds]}
        label="Feeds"
        placeholder="Search feeds…"
      />
      <div class="-mt-2">
        <Button variant="link" size="xs" icon="add" disabled={!quickCreate.can_create_feed} onclick={() => (feedOpen = true)}>Create New Feed</Button>
      </div>
      {#if errorFor('feed_ids')}<p class="text-error text-sm">{errorFor('feed_ids')}</p>{/if}
      <TextField bind:value={$form.ad.title} label="Title" name="title" error={errorFor('title')} required />
      <TextField bind:value={$form.ad.theme} label="Theme" name="theme" error={errorFor('theme')} />
      <TextField bind:value={$form.ad.savings} label="Estimated Savings" name="savings" placeholder="$10" error={errorFor('savings')} />
      <TextField bind:value={$form.ad.spend} label="Estimated Spend" name="spend" placeholder="$20" error={errorFor('spend')} />

      <fieldset class="fieldset">
        <legend class="fieldset-legend">Image Preference</legend>
        <div class="flex gap-4">
          {#each options.photo_preference_options ?? [] as option (option.value)}
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="photo_preference" class="radio radio-sm" value={option.value} bind:group={$form.ad.photo_preference} />
              {option.label}
            </label>
          {/each}
        </div>
      </fieldset>

      {#if $form.ad.photo_preference === 'custom'}
        <ImageSelector bind:value={$form.ad.ad_images} label="Campaign Images" placeId={$form.ad.place_id} />
      {/if}

      <ToggleField bind:checked={$form.ad.treasure_chest} label="Treasure Chest" name="treasure_chest" />
      <ToggleField bind:checked={$form.ad.display_as_new} label="Display as New" name="display_as_new" />
      {#if $form.ad.display_as_new}
        <DateField bind:value={$form.ad.display_as_new_until} label="Display as New Until" name="display_as_new_until" />
      {/if}
    </FormSection>

    <FormSection title="Adventure Details" description="The detail page for each Adventure this campaign advertises.">
      {#if $form.ad.adventure_details.length === 0}
        <p class="text-sm text-base-content/60">No Adventures selected — pick them in Select Adventures above and each gets its own detail here.</p>
      {:else}
        {#each $form.ad.adventure_details as detail, detailIndex (detail.adventure_id)}
          {#if detailIndex > 0}<div class="divider my-1"></div>{/if}
          <div class="space-y-4">
            <div class="flex items-center gap-2">
              <h3 class="text-sm font-semibold text-base-content/70 flex-1">
                {detail.title || `Adventure ${detailIndex + 1}`}{#if detailIndex === 0}<span class="font-normal text-base-content/50"> · first adventure</span>{/if}
              </h3>
              <Button variant="ghost" size="xs" square icon="chevron_up" aria-label="Move up" title="Move up" disabled={detailIndex === 0} onclick={() => moveAdventure(detailIndex, detailIndex - 1)} />
              <Button variant="ghost" size="xs" square icon="chevron_down" aria-label="Move down" title="Move down" disabled={detailIndex === $form.ad.adventure_details.length - 1} onclick={() => moveAdventure(detailIndex, detailIndex + 1)} />
            </div>
            <TextField bind:value={detail.title} label="Title" name={`adventure_detail_${detailIndex}_title`} error={errorFor('adventure_details.title')} required />
            <TextareaField bind:value={detail.description} label="Description" name={`adventure_detail_${detailIndex}_description`} error={errorFor('adventure_details.description')} />
            <TextField bind:value={detail.spend} label="Estimated Spend" name={`adventure_detail_${detailIndex}_spend`} placeholder="$20" />
            <TextField bind:value={detail.savings} label="Estimated Savings" name={`adventure_detail_${detailIndex}_savings`} placeholder="$10" />

            <fieldset class="fieldset">
              <legend class="fieldset-legend">Featured Photo</legend>
              <div class="flex gap-4">
                {#each options.featured_photo_preference_options ?? [] as option (option.value)}
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`adventure_detail_${detailIndex}_featured_photo_preference`}
                      class="radio radio-sm"
                      value={option.value}
                      checked={detail.featured_photo_preference === option.value}
                      onchange={() => setDetail(detailIndex, 'featured_photo_preference', option.value)}
                    />
                    {option.label}
                  </label>
                {/each}
              </div>
            </fieldset>

            <ImageSelector bind:value={detail.images} label="Adventure Images" placeId={$form.ad.place_id} />
          </div>
        {/each}
      {/if}
    </FormSection>

    <FormSection title="App Preview" description="How the campaign card and each adventure detail look in the app.">
      <AppPreview cards={preview} />
    </FormSection>

    <FormSection title="Select Audience">
      <div class="flex gap-4">
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="radio" class="radio radio-sm" checked={audienceType === 'everyone'} onclick={() => setAudienceType('everyone')} />
          Everyone
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="radio" class="radio radio-sm" checked={audienceType === 'custom'} onclick={() => setAudienceType('custom')} />
          Custom
        </label>
      </div>

      {#if audienceType === 'custom'}
        <MultiSelect bind:value={$form.ad.audience_names} options={audienceOptions} label="Audience" placeholder="Search audiences…" />

        {#if hasCsv}
          <div class="flex items-center gap-2 flex-wrap">
            <FileButton size="sm" icon="add" accept="text/csv" onchange={pickCsv}>Import from CSV</FileButton>
            <Button variant="default" size="sm" onclick={clearUsers}>Clear Users</Button>
            <Button variant="default" size="sm" disabled={$form.ad.user_ids.length === 0} onclick={() => (usersOpen = true)}>View Users</Button>
            <span class="text-sm text-base-content/60">{$form.ad.user_ids.length} users included.</span>
          </div>
          {#if csv.summary}<p class="text-sm text-base-content/60">{csv.summary}</p>{/if}
          {#if csv.error}<p class="text-error text-sm">{csv.error}</p>{/if}
          {#if errorFor('user_ids')}<p class="text-error text-sm">{errorFor('user_ids')}</p>{/if}
          <p class="text-xs text-base-content/50">Put the user ids in a "user_id" column, or in the first column.</p>
        {/if}
      {/if}
    </FormSection>

    <FormSection title="Schedule">
      <ScheduleEditor bind:value={$form.ad.schedule_attributes} timeZones={options.time_zones ?? []} error={errorFor('schedule')} />
      {#if $form.ad.schedule_attributes.recur_on === 'specific_days' && parentId}
        <p class="text-warning text-sm">
          This looks like it might be a campaign with a specific schedule. Make sure to add exclusion dates to the
          parent campaign or else both campaigns will display at the same time.
        </p>
      {/if}
    </FormSection>

    <FormSection title={isEdit ? 'Save Your Changes' : 'Set Live'}>
      <ToggleField bind:checked={$form.ad.active} label="Active" name="active" error={errorFor('active')} />
      <p class="text-sm text-base-content/60">
        {isEdit ? 'This campaign will be updated with the information above.' : 'This campaign will be set live.'}
      </p>
    </FormSection>

    {#if errorFor('base')}<p class="text-sm text-error">{errorFor('base')}</p>{/if}

    {#snippet footer()}
      <Button href={back_url} variant="ghost" size="sm">Cancel</Button>
      {#if !isEdit}
        <Button variant="secondary" outline size="sm" disabled={$form.processing} onclick={setAsInactive}>Save as inactive</Button>
      {/if}
      <SubmitButton processing={$form.processing} label={isEdit ? 'Update' : 'Set Live'} full={false} />
    {/snippet}
  </FormLayout>

  <AudienceUserList bind:open={usersOpen} userIds={$form.ad.user_ids} />
  <QuickCreateAdventureDrawer
    bind:open={adventureOpen}
    options={quickCreate}
    areaId={$form.ad.area_id}
    placeId={$form.ad.place_id}
    oncreated={onAdventureCreated}
  />
  <QuickCreateFeedDrawer bind:open={feedOpen} areaOptions={quickCreate.areas ?? []} areaId={$form.ad.area_id} oncreated={onFeedCreated} />
</Layout>
